import React from 'react';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Badge } from '@oysterjs/ui/badge';

import {
  ExcelSpreadsheetTemplateFieldsFragment,
  ExcelSpreadsheetTemplateState
} from '~/api/graphql';

import {
  CreatedByCell,
  EditLinkCell,
  TitleCell,
  UpdatedAtCell,
  selectionColumn
} from '~/components/list/library-table-cells';

export type ExcelTemplateSortKey = 'title' | 'user' | 'date' | 'state';

const EDIT_BASE = '/app/library/excel-templates';

const STATE_COLOR: Record<ExcelSpreadsheetTemplateState, 'green' | 'yellow' | 'red' | 'neutral'> = {
  [ExcelSpreadsheetTemplateState.Ready]: 'green',
  [ExcelSpreadsheetTemplateState.Queued]: 'yellow',
  [ExcelSpreadsheetTemplateState.Processing]: 'yellow',
  [ExcelSpreadsheetTemplateState.Failed]: 'red'
};

const DISABLED_EDIT_TITLE =
  'This Excel template cannot be edited while it is processing. Try again when the status is Ready or Failed.';

/**
 * An Excel template is editable in the library once ingestion has settled
 * (Ready / Failed). While Queued or Processing the row is shown but not
 * interactive, because the tab list it edits is still being discovered.
 */
export function isExcelTemplateEditable(template: ExcelSpreadsheetTemplateFieldsFragment): boolean {
  const state = template.state;
  if (!state) {
    return true;
  }
  return (
    state !== ExcelSpreadsheetTemplateState.Queued &&
    state !== ExcelSpreadsheetTemplateState.Processing
  );
}

/** rowHref helper: returns the edit URL only for editable templates. */
export function excelTemplateRowHref(
  template: ExcelSpreadsheetTemplateFieldsFragment
): string | undefined {
  return isExcelTemplateEditable(template) ? `${EDIT_BASE}/${template.id}` : undefined;
}

export function getExcelTemplateColumns(opts: {
  selectedSet: ReadonlySet<string>;
  setSelectedExcelTemplates: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: ExcelSpreadsheetTemplateFieldsFragment[];
  /**
   * Whether the caller may change these rows. False drops the bulk-selection
   * column and turns the row link from "Edit" into "View".
   */
  canEdit?: boolean;
}): ColumnDef<ExcelSpreadsheetTemplateFieldsFragment, ExcelTemplateSortKey, never>[] {
  const { selectedSet, setSelectedExcelTemplates, filtered } = opts;
  return [
    ...(opts.canEdit === false
      ? []
      : [
          selectionColumn<ExcelSpreadsheetTemplateFieldsFragment, ExcelTemplateSortKey>({
            selectedSet,
            setSelected: setSelectedExcelTemplates,
            filtered,
            pluralLabel: 'Excel templates'
          })
        ]),
    {
      id: 'title',
      header: 'Title',
      sortKey: 'title',
      renderCell: (row) => <TitleCell title={row.title} visibility={row.visibility} />
    },
    {
      id: 'state',
      header: 'Status',
      sortKey: 'state',
      renderCell: (row) => {
        const state = row.state ?? ExcelSpreadsheetTemplateState.Ready;
        return <Badge color={STATE_COLOR[state] ?? 'neutral'}>{state}</Badge>;
      }
    },
    {
      id: 'tabs',
      header: 'Tabs',
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      renderCell: (row) => (
        <span className="text-neutral-500 dark:text-neutral-400">{row.tabs.length || '—'}</span>
      )
    },
    {
      id: 'user',
      header: 'Created By',
      sortKey: 'user',
      renderCell: (row) => <CreatedByCell name={row.createdBy.name} />
    },
    {
      id: 'date',
      header: 'Last Updated',
      sortKey: 'date',
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      renderCell: (row) => <UpdatedAtCell value={row.updatedAt} />
    },
    {
      id: 'actions',
      header: 'Actions',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderCell: (row) => (
        <EditLinkCell
          canEdit={opts.canEdit}
          href={`${EDIT_BASE}/${row.id}`}
          disabledTitle={isExcelTemplateEditable(row) ? undefined : DISABLED_EDIT_TITLE}
        />
      )
    }
  ];
}

export function excelTemplateSortFn(
  data: readonly ExcelSpreadsheetTemplateFieldsFragment[],
  sortKey: ExcelTemplateSortKey,
  direction: 'asc' | 'desc'
): ExcelSpreadsheetTemplateFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortKey === 'user') {
      return (a.createdBy.name || '').localeCompare(b.createdBy.name || '');
    }
    if (sortKey === 'state') {
      return (a.state ?? '').localeCompare(b.state ?? '');
    }
    const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return aDate - bDate;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}
